import { create } from "zustand";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useAppLoading } from "./useAppLoading";
import { IUser } from "./useUsers";
import { useCurrentLocation } from "./useCurrentLocation";
import { useAlert } from "./useAlert";

interface ILotteryDetails {
  _id: string;
  partner: string;
  en_title: string;
  nl_title: string;
  en_description: string;
  nl_description: string;
  image: string;
  thumbnail: string;
  en_prizeTitle: string;
  nl_prizeTitle: string;
  en_prizeDescription: string;
  nl_prizeDescription: string;
  drawDate: Date;
  status: "PENDING" | "ACTIVE" | "COMPLETED";
  winners: string[];
  numberOfWinners?: number;
  isRecurring?: boolean;
  recurringInterval?: "DAILY" | "WEEKLY" | "MONTHLY";
  acceptingMaterials?: string[];
  includeStatiegeldDrops?: boolean;
  cities?: string[];
  locationType?: "CITY" | "LOCATION";
  locationIds?: string[];
  allowMultipleEntries?: boolean;
  createdAt: Date;
  // Drop-specific fields
  isFirstTicketDrop?: boolean;
  userTicketCount?: number;
}

interface IDrop {
  _id?: string;
  user?: IUser;
  totalWeight?: number;
  totalPrice?: number;
  dropComposition?: IDropComposition[];
  status?: "approved" | "declined" | "pending";
  location?: string;
  dropFeedback?: {
    user: IUser;
    drop?: string;
    comment?: string;
    image?: string;
  }[];
  qrCode?: string;
  lotteryDetails?: ILotteryDetails;
  lotteryEligible?: boolean;
  lotteryId?: string;
  lotteryJoined?: boolean;
  joinedLottery?: ILotteryDetails[];
  rejectedLottery?: ILotteryDetails[];
  createdAt?: Date;
  updatedAt?: Date;
}
interface IDropComposition {
  _id?: string;
  drop?: string;
  type?: string;
  weight?: number;
  price?: number;
  /** What the line was priced with at drop time (rate actually paid). */
  priceSnapshot?: { rate?: number | null } | null;
}

interface ICreateDrop {
  user?: string;
  totalWeight?: number;
  totalPrice?: number;
  dropComposition?: IDropComposition[];
  status?: "approved" | "declined" | "pending";
  location?: string;
}

interface IDropsStats {
  approved: number;
  declined: number;
  pending: number;
}

interface IStore {
  /** State */
  drops: IDrop[];
  dropStatusStats: IDropsStats;
  randomDrop?: IDrop | null;
  dropsLoading: boolean;
  dropsInitialized: boolean;
  hasNextPage: boolean;
  hasNextUserDrops: boolean;
  userDrops: IDrop[];
  oneDrop: IDrop | null;
  highlightedStatus: "Pending" | "Approved" | "Declined" | null;
  createdDrop: string | null;
  updatedDrop: string | null;
  deletedDrop: string | null;
  searchText: string;
  /** Drops page location filter: "all" or location id */
  dropsLocationFilter: string;
  setDropsLocationFilter: (location: string) => void;
  /** Callbacks */
  downloadDropsInCSV: () => void;
  downloadDropsInCSVByProduct: (marketPriceId: string) => void;
  getDropStatusStats: () => void;
  getDrops: (page?: number, limit?: number, search?: string) => void;
  getUserDrops: (userId: string, page?: number, limit?: number) => void;
  getOneDrop: (dropId: string) => void;
  getRandomDrop: (dropId: string) => void;
  createDrop: (drop: ICreateDrop, comment?: string) => void;
  createDropFeedback: (dropId: string, comment: string, image?: string) => void;
  updateDrop: (dropId: string, drop: IDrop) => void;
  deleteDrop: (dropId: string, userId?: string) => void;
  setSearchText: (searchText: string) => void;
  setHighlightedStatus: (
    status: "Pending" | "Approved" | "Declined" | null
  ) => void;
  /** Clear callbacks */
  clearUserDrops: () => void;
  clearCreatedDrop: () => void;
  clearUpdatedDrop: () => void;
  clearDeletedDrop: () => void;
  clearSearchText: () => void;
}

export const useDrops = create<IStore>()((set, get) => ({
  /** State */
  drops: [],
  dropStatusStats: {
    approved: 0,
    declined: 0,
    pending: 0,
  },
  randomDrop: null,
  dropsLoading: false,
  dropsInitialized: false,
  hasNextPage: false,
  hasNextUserDrops: false,
  userDrops: [],
  highlightedStatus: null,
  oneDrop: null,
  createdDrop: null,
  updatedDrop: null,
  deletedDrop: null,
  searchText: "",
  dropsLocationFilter: "all",
  setDropsLocationFilter: (location) => set({ dropsLocationFilter: location }),
  /** Callbacks */
  downloadDropsInCSVByProduct: async (marketPriceId) => {
    useAppLoading.getState().setIsLoading(true);
    const location = get().dropsLocationFilter;
    try {
      const response = await axios.get(
        `drops/csv/${marketPriceId}?location=${location}`
      );
      if (response?.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "drops.csv");
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      LOG.error("Error on downloading drops in CSV", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  downloadDropsInCSV: async () => {
    useAppLoading.getState().setIsLoading(true);
    const location = get().dropsLocationFilter;
    try {
      const response = await axios.get(`drops/csv?location=${location}`);
      if (response?.data) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "drops.csv");
        document.body.appendChild(link);
        link.click();
      }
    } catch (error) {
      LOG.error("Error on downloading drops in CSV", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getDropStatusStats: async () => {
    const location = get().dropsLocationFilter;
    try {
      const response = await axios.get(
        `drops/status-stats?location=${location}`
      );
      if (response?.data) {
        set({ dropStatusStats: response.data });
      }
    } catch (error) {
      LOG.error("Error on fetching drop status stats", { error });
    }
  },
  setHighlightedStatus: (status) => {
    set({ highlightedStatus: status });
  },
  getDrops: async (page = 1, limit = 20, search) => {
    useAppLoading.getState().setIsLoading(true);
    set({ dropsLoading: true });
    const location = get().dropsLocationFilter;
    try {
      const highlightedStatus = get().highlightedStatus;
      const response = await axios.get(
        `drops?page=${page}&limit=${limit}&search=${search ?? ""}&highlightedStatus=${highlightedStatus?.toLowerCase() ?? ""}&location=${location}`
      );
      if (response?.data) {
        set((state) => ({
          ...state,
          hasNextPage: response?.data?.length === limit,
          drops:
            page === 1 ? response?.data : [...state.drops, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on getting drops", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      set({ dropsLoading: false, dropsInitialized: true });
    }
  },
  getUserDrops: async (userId, page = 1, limit = 10) => {
    useAppLoading.getState().setIsLoading(true);
    const currentLocation = useCurrentLocation.getState().currentLocation;
    try {
      const response = await axios.get(
        `drops/user/${userId}?page=${page}&limit=${limit}&location=${currentLocation}`
      );
      if (response?.data) {
        set((state) => ({
          hasNextUserDrops: response?.data?.length === limit,
          userDrops:
            page === 1
              ? response?.data
              : [...state.userDrops, ...response?.data],
        }));
      }
    } catch (error) {
      LOG.error("Error on getting user drops", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  getRandomDrop: async (dropId) => {
    try {
      const response = await axios.get(`drops/${dropId}`);
      if (response?.data && !response?.data?.message) {
        set({ randomDrop: response?.data });
      }
    } catch (error) {
      LOG.error("Error on getting random drop", { error });
    }
  },
  createDrop: async (drop, comment) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.post("/drops", drop);
      if (response?.data) {
        if (comment !== "") {
          get().createDropFeedback(response?.data?._id, comment as string);
        }
        set({ createdDrop: response?.data?.drop?._id });
      }
    } catch (error) {
      // @ts-ignore
      if (error?.data?.message) {
        useAlert.getState().setAlert({
          isOpen: true,
          // @ts-ignore
          message: error?.data?.message,
          severity: "error",
        });
      }
      LOG.error("Error on creating a drop", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  createDropFeedback: async (dropId, comment, image) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      let body: any = { comment };
      if (image) {
        body.image = image;
      }
      await axios.post(`/drops/feedback/${dropId}`, body);
    } catch (error) {
      LOG.error("Error on creating a drop feedback", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      get().getOneDrop(dropId);
    }
  },
  updateDrop: async (dropId, drop) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.put(`/drops/${dropId}`, drop);
      if (response?.data) {
        set({ updatedDrop: dropId });
      }
    } catch (error) {
      LOG.error("Error on updating a drop", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      get().getDrops();
    }
  },
  getOneDrop: async (dropId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.get(`drops/${dropId}`);
      if (response?.data) {
        set({ oneDrop: response?.data });
      }
    } catch (error) {
      LOG.error("Error on getting one drop", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
    }
  },
  deleteDrop: async (dropId, userId) => {
    useAppLoading.getState().setIsLoading(true);
    try {
      const response = await axios.delete(`/drops/${dropId}`);
      if (response?.data) {
        set((state) => ({
          drops: state.drops.filter((drop) => drop._id !== dropId),
          deletedDrop: dropId,
        }));
      }
    } catch (error) {
      LOG.error("Error on deleting a drop", { error });
    } finally {
      useAppLoading.getState().setIsLoading(false);
      if (userId) {
        get().getUserDrops(userId);
      }
    }
  },
  setSearchText: (searchText) => {
    set({ searchText });
  },
  /** Clear callbacks */
  clearUserDrops: () => {
    set({ userDrops: [] });
  },
  clearCreatedDrop: () => {
    set({ createdDrop: null });
  },
  clearUpdatedDrop: () => {
    set({ updatedDrop: null });
  },
  clearDeletedDrop: () => {
    set({ deletedDrop: null });
  },
  clearSearchText: () => {
    set({ searchText: "" });
  },
}));
